import { alpha, Card, Typography, styled, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { SteppedLoader as SteppedLoaderTypes } from '@/schemas/sceneDataSchema';
import { SteppedLoaderImageKeys } from '@/constants/loaderTypes';
import avifCar from '@/assets/images/stepped-loader/3d-icon-car.avif';
import avifDocs from '@/assets/images/stepped-loader/3d-icon-docs.avif';
import avifShield from '@/assets/images/stepped-loader/3d-icon-shield.avif';
import avifPrice from '@/assets/images/stepped-loader/3d-icon-price.avif';
import avifHyundaiCar from '@/assets/images/elantra_front.avif';
import pngCar from '@/assets/images/stepped-loader/3d-icon-car.png';
import pngDocs from '@/assets/images/stepped-loader/3d-icon-docs.png';
import pngShield from '@/assets/images/stepped-loader/3d-icon-shield.png';
import pngPrice from '@/assets/images/stepped-loader/3d-icon-price.png';
import pngHyundaiCar from '@/assets/images/elantra_front.png';

const CircleCheck = () => (
  <svg
    className='circle-check'
    xmlns='http://www.w3.org/2000/svg'
    viewBox='100 -860 760 760'
    fill='currentColor'
  >
    <path d='M421-311.46 690.54-581l-34.85-34.23L421-380.15 302.54-498.61l-33.85 34.23L421-311.46ZM480.07-100q-78.22 0-147.4-29.92t-120.99-81.71q-51.81-51.79-81.75-120.94Q100-401.71 100-479.93q0-78.84 29.92-148.21t81.71-120.68q51.79-51.31 120.94-81.25Q401.71-860 479.93-860q78.84 0 148.21 29.92t120.68 81.21q51.31 51.29 81.25 120.63Q860-558.9 860-480.07q0 78.22-29.92 147.4t-81.21 120.99q-51.29 51.81-120.63 81.75Q558.9-100 480.07-100Z' />
  </svg>
);

interface StepCardProps {
  png: string;
  avif: string;
  text: string;
  isActive: boolean;
  isLastStep: boolean;
  duration: number;
}

function StepCard({ png, avif, text, duration, isActive, isLastStep }: StepCardProps) {
  const [showCheck, setShowCheck] = useState(false);
  const circularProgressSize = 60;

  useEffect(() => {
    if (!isActive) return;
    if (isLastStep) return; // Last step is indeterminate and should never show a checkmark

    const showCheckTimer = setTimeout(() => {
      setShowCheck(true);
    }, duration - 500);

    return () => clearTimeout(showCheckTimer);
  }, [isActive, isLastStep, duration]);

  return (
    <StyledCard isActive={isActive}>
      <CircularProgressContainer
        showCheck={showCheck}
        circularProgressSize={circularProgressSize}
      >
        {isActive && (
          <CircularProgress
            size={circularProgressSize}
            thickness={3}
          />
        )}
        <Picture>
          <source
            srcSet={avif}
            type='image/avif'
          />
          <img src={png} />
        </Picture>
        <CircleCheck />
      </CircularProgressContainer>
      <Typography variant='h4'>{text}</Typography>
    </StyledCard>
  );
}

interface Image {
  png: string;
  avif: string;
}

const images: Record<SteppedLoaderImageKeys, Image> = {
  [SteppedLoaderImageKeys.DOCS]: {
    png: pngDocs,
    avif: avifDocs,
  },
  [SteppedLoaderImageKeys.CAR]: {
    png: pngCar,
    avif: avifCar,
  },
  [SteppedLoaderImageKeys.HYUNDAI_CAR]: {
    png: pngHyundaiCar,
    avif: avifHyundaiCar,
  },
  [SteppedLoaderImageKeys.SHIELD]: {
    png: pngShield,
    avif: avifShield,
  },
  [SteppedLoaderImageKeys.PRICE]: {
    png: pngPrice,
    avif: avifPrice,
  },
};

export function SteppedLoader({ steps }: Omit<SteppedLoaderTypes, 'type'>) {
  const [currentStep, setCurrentStep] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  // This useEffect handles the initial state where all cards begin as inactive
  useEffect(() => {
    const startDelay = 400;

    const initialTimer = setTimeout(() => {
      setHasStarted(true);
    }, startDelay);

    return () => clearTimeout(initialTimer);
  }, []);

  // This useEffect handles the progression of highlighting each card
  useEffect(() => {
    if (!hasStarted) return;

    const stepDuration = steps[currentStep].duration;

    const stepTimer = setTimeout(() => {
      setCurrentStep((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
    }, stepDuration);

    return () => clearTimeout(stepTimer);
  }, [steps, currentStep, hasStarted]);

  return (
    <Container>
      {steps.map((step, index) => {
        const imageKey = step.image;
        const image = images[imageKey];

        return (
          <StepCard
            key={index}
            png={image?.png ?? ''}
            avif={image?.avif ?? ''}
            text={step.text}
            duration={step.duration}
            isActive={hasStarted && index === currentStep}
            isLastStep={index === steps.length - 1}
          />
        );
      })}
    </Container>
  );
}

const Container = styled('div')`
  --spacing: 16px;

  display: flex;
  flex-direction: column;
  gap: var(--spacing);
`;

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing);
  padding: var(--spacing);
  border: 1px solid var(--mui-palette-border-secondary);
  border-radius: var(--border-radius-medium);
  opacity: ${({ isActive }) => (isActive ? 1 : 0.3)};
  transform: scale(${({ isActive }) => (isActive ? 1.04 : 1)});
  transition: all 400ms var(--ease-out-quart);
`;
const CircularProgressContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'showCheck' && prop !== 'circularProgressSize',
})<{ showCheck: boolean; circularProgressSize: number }>`
  width: ${({ circularProgressSize }) => circularProgressSize}px;
  height: ${({ circularProgressSize }) => circularProgressSize}px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;

  .circle-check {
    position: absolute;
    inset: 0;
    color: var(--mui-palette-success-main);
    background-color: var(--mui-palette-background-main);
    border-radius: 999px;
    transition:
      transform 400ms cubic-bezier(0.175, 0.885, 0.32, 1.275),
      opacity 200ms ease-out;
    opacity: ${({ showCheck }) => (showCheck ? 1 : 0)};
    transform: scale(${({ showCheck }) => (showCheck ? 1 : 0.7)}) rotate(${({ showCheck }) => (showCheck ? 0 : 30)}deg);
  }
`;

const Picture = styled('picture')`
  position: absolute;
  inset: 4px;
  background-color: ${({ theme }) => alpha(theme.palette.primary.main, 0.125)};
  border-radius: 999px;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 999px;
  }
`;
